<template>
  <base-modal @close="handleClose()">
    <template #header>
      <div class="er-pay-modal">
        <div class="modal-header">
          <img
            @click.stop="handleClose()"
            class="close-image"
            src="../../../public/custom/images/cross.svg"
            alt="Cross image"
          >
        </div>
        <div class="modal-title">
          {{ $t('message.profile.borrower.earlyRepayment.payModal.title') }}
        </div>
        <div class="calc-loading" v-if="isCalculating">
          <base-spinner :is-loading="true" :is-local="true" />
        </div>
        <template v-if="!isCalculating && calculation?.amounts">
          <div class="breakdown-list">
            <div class="breakdown-item">
              <span class="breakdown-label">{{ $t('message.profile.borrower.earlyRepayment.payModal.principal') }}</span>
              <span class="breakdown-value">{{ calculation.amounts.principal }} {{ currency }}</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">
                {{ $t('message.profile.borrower.earlyRepayment.payModal.interest', { days: calculation.actualDays }) }}
              </span>
              <span class="breakdown-value">{{ calculation.amounts.interest }} {{ currency }}</span>
            </div>
            <div class="breakdown-item breakdown-total">
              <span class="breakdown-label">{{ $t('message.profile.borrower.earlyRepayment.payModal.total') }}</span>
              <span class="breakdown-value">{{ calculation.amounts.total }} {{ currency }}</span>
            </div>
          </div>
          <div class="modal-buttons">
            <div class="modal-pay-btn">
              <base-button
                :text="$t('message.profile.borrower.earlyRepayment.payModal.payBtn')"
                :min-height="'40px'"
                :disabled="isPayDisabled"
                :is-loading="isPaying"
                @click="startPayment()"
              />
            </div>
          </div>
        </template>
      </div>
    </template>
  </base-modal>
</template>

<script>
import BaseModal from '../base/BaseModal.vue';
import BaseButton from '../base/BaseButton.vue';
import BaseSpinner from '../base/BaseSpinner.vue';
import { resolveEarlyRepaymentErrorMessage } from '../../helpers/earlyRepayment';

export default {
  name: 'EarlyRepaymentPayModal',
  inject: ['$httpService'],
  components: {
    BaseModal,
    BaseButton,
    BaseSpinner,
  },
  props: {
    loanPublicId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      calculation: null,
      isCalculating: true,
      isPaying: false,
    };
  },
  computed: {
    currency() {
      return this.$t('message.profile.borrower.activeOffers.payModal.currency');
    },
    isPayDisabled() {
      return this.isPaying || !this.calculation?.totalMinor;
    },
  },
  async created() {
    await this.loadCalculation();
  },
  methods: {
    handleClose() {
      if (!this.isPaying) {
        this.$emit('close');
      }
    },
    async loadCalculation() {
      this.isCalculating = true;

      try {
        const response = await this.$httpService.loan.calculateEarlyRepayment(this.loanPublicId);
        this.calculation = response.data;
      } catch (error) {
        this.$toast.error(resolveEarlyRepaymentErrorMessage(error, (key, params) => this.$t(key, params)));
        this.$emit('close');
      } finally {
        this.isCalculating = false;
      }
    },
    async startPayment() {
      if (this.isPayDisabled) {
        return;
      }

      this.isPaying = true;

      try {
        const response = await this.$httpService.loan.payEarlyRepayment({
          loan_public_id: this.loanPublicId,
          amount: this.calculation.totalMinor,
        });

        this.$emit('success', response.data);
      } catch (error) {
        const data = error?.response?.data || {};

        if (data.message === 'loan.early-repayment.amount-mismatch') {
          this.$toast.error(this.$t('message.profile.borrower.earlyRepayment.errors.amountMismatchDetail', {
            expected: data.expected || '',
          }));
          await this.loadCalculation();
        } else {
          this.$toast.error(resolveEarlyRepaymentErrorMessage(error, (key, params) => this.$t(key, params)));

          if (data.message === 'user.add-card' || data.message === 'card.src.not-found') {
            this.$router.push({ name: 'cards' });
          }
        }
      } finally {
        this.isPaying = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.er-pay-modal {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 40px;
}

.modal-header {
  @include modal-close-block;
  width: 100%;
}

.close-image {
  cursor: pointer;
}

.modal-title {
  margin-bottom: 24px;
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
  text-align: center;
  color: #101010;
}

.calc-loading {
  width: 100%;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.breakdown-list {
  width: 100%;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  font-size: 16px;
  line-height: 22px;
  color: #333333;
}

.breakdown-label {
  color: #878787;
}

.breakdown-value {
  font-weight: 600;
  color: #121212;
  white-space: nowrap;
}

.breakdown-total {
  padding-top: 16px;
  border-top: 1px solid #eceef0;

  .breakdown-label,
  .breakdown-value {
    font-weight: 600;
    font-size: 18px;
    color: #1a8d48;
  }
}

.modal-buttons {
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.modal-pay-btn {
  width: 280px;
  max-width: 100%;
  height: 40px;
  flex-shrink: 0;
}
</style>
